/**
 * Flyer — one class, three behaviours selected by `props.variant` (Brief 007 P2).
 *
 * Variants:
 *   'bat'  — hangs at anchor; swoops toward player when within range; returns.
 *            Sprite: 'flyer_bat_hang' while idle, 'flyer_bat_fly' while moving.
 *   'bee'  — horizontal patrol ±range around anchor.x at constant speed.
 *            Sprite: 'flyer_bee_fly'. Facing tracks direction.
 *   'fly'  — slow horizontal drift + vertical triangle-wave bob ±amplitude.
 *            Sprite: 'flyer_fly_fly'. (Brief called for sine; substituted
 *            deterministic triangle wave — sine is lint-banned.)
 *
 * Combat (all variants): stomp-to-kill with bounce (player falling + feet near top);
 * any other contact hurts the player. Reuses Goomba's overlap rule verbatim.
 * A squashTimer keeps the sprite visible briefly after death.
 *
 * Determinism: Math.abs/floor/min/max/sign/round only — ESLint-clean under
 * src/engine/. No Math.sin, no Math.random, no Date.now.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

// ── Shared constants ──────────────────────────────────────────────────────────

/** Seconds the squashed sprite lingers after death (same window as Goomba). */
const SQUASH_SECONDS = 0.4;

// ── Bat constants ─────────────────────────────────────────────────────────────

const BAT_SWOOP_SPEED = 160; // px/s while swooping/returning
const BAT_DEFAULT_RANGE = 320; // px — horizontal trigger radius

// ── Bee constants ─────────────────────────────────────────────────────────────

const BEE_SPEED = 80; // px/s horizontal patrol speed
const BEE_DEFAULT_RANGE = 240; // px — half-width of patrol corridor

// ── Fly constants ─────────────────────────────────────────────────────────────

const FLY_DRIFT_SPEED = 45; // px/s horizontal slow drift
const FLY_DEFAULT_AMPLITUDE = 24; // px — triangle-wave bob half-range
const FLY_BOB_PERIOD = 1.8; // seconds per full bob cycle
const FLY_DRIFT_RANGE = 80; // px — drift turns around at ±this from anchor.x

type BatState = 'HANG' | 'SWOOP' | 'RETURN';
type FlyerVariant = 'bat' | 'bee' | 'fly';

export class Flyer implements Entity {
  readonly type = EntityType.FLYER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney bat/bee/fly art faces left
  alive = true;
  squashTimer = 0;

  private variant: FlyerVariant;

  // ── Bat state ──────────────────────────────────────────────────────────────
  private batState: BatState = 'HANG';
  private batRange: number;
  /** Target position set at the start of a swoop (player position snapshot). */
  private swoopTargetX = 0;
  private swoopTargetY = 0;

  // ── Bee state ─────────────────────────────────────────────────────────────
  private beeRange: number;

  // ── Fly state ─────────────────────────────────────────────────────────────
  private flyAmplitude: number;
  private flyBobPhase = 0;
  private flyDriftDir: 1 | -1 = 1;

  // ── Shared anchor (world px center) ───────────────────────────────────────
  private anchorX: number;
  private anchorY: number;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  bottom of the entity's tile cell (world px)
   * @param props  variant / range / amplitude
   */
  constructor(cx: number, feetY: number, props?: Record<string, unknown>) {
    const variant = (props?.variant as string | undefined) ?? 'bee';
    this.variant = (variant === 'bat' || variant === 'bee' || variant === 'fly')
      ? variant : 'bee';

    const W = 52;
    const H = 44;
    // Center body on spawn point; anchor is center.
    const centerY = feetY - H / 2 - 20; // float ~20 px above the tile floor center
    this.anchorX = cx;
    this.anchorY = centerY;
    this.body = { x: cx - W / 2, y: centerY - H / 2, w: W, h: H };

    // Per-variant defaults.
    if (this.variant === 'bat') {
      this.batRange = typeof props?.range === 'number' ? props.range : BAT_DEFAULT_RANGE;
    } else if (this.variant === 'bee') {
      this.beeRange = typeof props?.range === 'number' ? props.range : BEE_DEFAULT_RANGE;
      // Start moving left.
      this.vx = -BEE_SPEED;
      this.facing = -1;
    } else {
      this.flyAmplitude = typeof props?.amplitude === 'number' ? props.amplitude : FLY_DEFAULT_AMPLITUDE;
      this.vx = FLY_DRIFT_SPEED * this.flyDriftDir;
    }
    // Satisfy TS: init fields that may not have been set in this branch.
    this.batRange ??= BAT_DEFAULT_RANGE;
    this.beeRange ??= BEE_DEFAULT_RANGE;
    this.flyAmplitude ??= FLY_DEFAULT_AMPLITUDE;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) {
      this.squashTimer = Math.max(0, this.squashTimer - dt);
      return;
    }
    switch (this.variant) {
      case 'bat':  this.updateBat(dt, ctx); break;
      case 'bee':  this.updateBee(dt); break;
      case 'fly':  this.updateFly(dt); break;
    }
  }

  // ── Bat movement ────────────────────────────────────────────────────────────

  private updateBat(dt: number, ctx: EntityContext): void {
    const px = ctx.player.body.x + ctx.player.body.w / 2;
    const py = ctx.player.body.y + ctx.player.body.h / 2;

    switch (this.batState) {
      case 'HANG': {
        // Hover at anchor — tiny vx/vy drift already zeroed; just check trigger.
        const dx = px - this.anchorX;
        if (Math.abs(dx) < this.batRange) {
          // Snapshot player position as swoop target.
          this.swoopTargetX = px;
          this.swoopTargetY = py;
          this.batState = 'SWOOP';
          this.facing = dx >= 0 ? 1 : -1;
        }
        break;
      }
      case 'SWOOP': {
        const dx = this.swoopTargetX - (this.body.x + this.body.w / 2);
        const dy = this.swoopTargetY - (this.body.y + this.body.h / 2);
        const dist = Math.sqrt(dx * dx + dy * dy); // sqrt allowed (not transcendental ban)
        if (dist < 8) {
          // Arrived — start returning.
          this.batState = 'RETURN';
          this.facing = this.anchorX >= this.body.x + this.body.w / 2 ? 1 : -1;
        } else {
          // Move toward target at swoop speed.
          const invDist = 1 / dist;
          this.vx = dx * invDist * BAT_SWOOP_SPEED;
          this.vy = dy * invDist * BAT_SWOOP_SPEED;
          this.body.x += this.vx * dt;
          this.body.y += this.vy * dt;
          this.facing = this.vx >= 0 ? 1 : -1;
        }
        break;
      }
      case 'RETURN': {
        const dx = this.anchorX - (this.body.x + this.body.w / 2);
        const dy = this.anchorY - (this.body.y + this.body.h / 2);
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 8) {
          // Snapped back to anchor.
          this.body.x = this.anchorX - this.body.w / 2;
          this.body.y = this.anchorY - this.body.h / 2;
          this.vx = 0;
          this.vy = 0;
          this.batState = 'HANG';
        } else {
          const invDist = 1 / dist;
          this.vx = dx * invDist * BAT_SWOOP_SPEED;
          this.vy = dy * invDist * BAT_SWOOP_SPEED;
          this.body.x += this.vx * dt;
          this.body.y += this.vy * dt;
          this.facing = this.vx >= 0 ? 1 : -1;
        }
        break;
      }
    }
  }

  // ── Bee movement ─────────────────────────────────────────────────────────────

  private updateBee(dt: number): void {
    // Horizontal oscillation bounded by ±beeRange from anchor.
    this.body.x += this.vx * dt;
    const centerX = this.body.x + this.body.w / 2;
    if (centerX > this.anchorX + this.beeRange) {
      // Past right bound — clamp and reverse.
      this.body.x = this.anchorX + this.beeRange - this.body.w / 2;
      this.vx = -BEE_SPEED;
      this.facing = -1;
    } else if (centerX < this.anchorX - this.beeRange) {
      // Past left bound — clamp and reverse.
      this.body.x = this.anchorX - this.beeRange - this.body.w / 2;
      this.vx = BEE_SPEED;
      this.facing = 1;
    } else {
      this.facing = this.vx >= 0 ? 1 : -1;
    }
  }

  // ── Fly movement ──────────────────────────────────────────────────────────────

  private updateFly(dt: number): void {
    // Slow horizontal drift, bounces at ±FLY_DRIFT_RANGE from anchor.
    this.body.x += this.vx * dt;
    const centerX = this.body.x + this.body.w / 2;
    if (centerX > this.anchorX + FLY_DRIFT_RANGE) {
      this.body.x = this.anchorX + FLY_DRIFT_RANGE - this.body.w / 2;
      this.flyDriftDir = -1;
      this.vx = FLY_DRIFT_SPEED * this.flyDriftDir;
    } else if (centerX < this.anchorX - FLY_DRIFT_RANGE) {
      this.body.x = this.anchorX - FLY_DRIFT_RANGE - this.body.w / 2;
      this.flyDriftDir = 1;
      this.vx = FLY_DRIFT_SPEED * this.flyDriftDir;
    }
    this.facing = this.vx >= 0 ? 1 : -1;

    // Triangle-wave vertical bob (substituted for banned Math.sin).
    // triangle(t in [0,1)) = 1 - |2t - 1|  → [0..1..0]
    this.flyBobPhase = (this.flyBobPhase + dt) % FLY_BOB_PERIOD;
    const t = this.flyBobPhase / FLY_BOB_PERIOD;
    const triangle = 1 - Math.abs(2 * t - 1); // [0..1]
    const bob = this.flyAmplitude * (triangle * 2 - 1); // [-amp .. +amp]
    this.body.y = this.anchorY - this.body.h / 2 + bob;
  }

  // ── Combat ────────────────────────────────────────────────────────────────────

  onPlayerOverlap(ctx: EntityContext): void {
    if (!this.alive) return;
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    // Stomp rule (verbatim Goomba): player falling AND feet near top of body — and only
    // when the archetype grants offense (PRECISION = pure hazard → falls through to hurt).
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
    this.vy = 0;
  }

  // ── Rendering ─────────────────────────────────────────────────────────────────

  spriteKey(): string {
    if (this.variant === 'bat') {
      // Show hang sprite while idle; fly sprite while swooping/returning.
      return this.batState === 'HANG' ? 'flyer_bat_hang' : 'flyer_bat_fly';
    }
    if (this.variant === 'bee') return 'flyer_bee_fly';
    return 'flyer_fly_fly';
  }

  visible(): boolean { return this.alive || this.squashTimer > 0; }
}
